import { useState, useEffect, useCallback } from "react";
import { Card, CardContent } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { useAuth } from "../contexts/AuthContext";
import { overtimeAPI } from "../lib/api";
import { formatHours } from "../lib/overtime";
import { OvertimeCalendar } from "../components/employee/OvertimeCalendar";
import { TimeInLieu } from "../components/employee/TimeInLieu";
import { TeamOverview } from "../components/employee/TeamOverview";
import { CalendarClock, Clock, TimerOff, Scale, Hourglass } from "lucide-react";
import { toast } from "sonner";

function StatCard({ icon: Icon, label, value, accent, testId }) {
  return (
    <Card data-testid={testId}>
      <CardContent className="pt-5 pb-4">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${accent}`}>
            <Icon className="w-5 h-5 text-white" />
          </div>
          <div>
            <p className="text-xs text-slate-500">{label}</p>
            <p className="text-xl font-bold text-slate-900">{value}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function Employee() {
  const { user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [entries, setEntries] = useState([]);
  const [lieuRequests, setLieuRequests] = useState([]);

  const loadData = useCallback(async () => {
    try {
      const [s, e, l] = await Promise.all([
        overtimeAPI.getMySummary(),
        overtimeAPI.getMyEntries(),
        overtimeAPI.getMyLieuRequests(),
      ]);
      setSummary(s.data);
      setEntries(e.data);
      setLieuRequests(l.data);
    } catch (err) {
      toast.error("Failed to load overtime data");
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const isSuperAdmin = !!user?.is_super_admin;

  return (
    <div className="space-y-6" data-testid="employee-page">
      <div>
        <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-2">
          <CalendarClock className="w-7 h-7 text-blue-600" />
          Employee
        </h1>
        <p className="text-slate-600 mt-1">Track your overtime and request time in lieu</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Clock} label="Total Overtime" value={formatHours(summary?.total_overtime)} accent="bg-blue-600" testId="employee-stat-total" />
        <StatCard icon={TimerOff} label="Time in Lieu Taken" value={formatHours(summary?.approved_lieu)} accent="bg-emerald-600" testId="employee-stat-lieu" />
        <StatCard icon={Scale} label="Overtime Balance" value={formatHours(summary?.balance)} accent="bg-violet-600" testId="employee-stat-balance" />
        <StatCard icon={Hourglass} label="Pending Requests" value={summary?.pending_requests ?? 0} accent="bg-amber-500" testId="employee-stat-pending" />
      </div>

      <Tabs defaultValue="calendar">
        <TabsList>
          <TabsTrigger value="calendar" data-testid="employee-tab-calendar">My Overtime</TabsTrigger>
          <TabsTrigger value="lieu" data-testid="employee-tab-lieu">Time in Lieu</TabsTrigger>
          {isSuperAdmin && <TabsTrigger value="team" data-testid="employee-tab-team">Team Overview</TabsTrigger>}
        </TabsList>

        <TabsContent value="calendar" className="pt-4">
          <OvertimeCalendar entries={entries} onChanged={loadData} />
        </TabsContent>

        <TabsContent value="lieu" className="pt-4">
          <TimeInLieu requests={lieuRequests} balance={summary?.balance ?? 0} onChanged={loadData} />
        </TabsContent>

        {isSuperAdmin && (
          <TabsContent value="team" className="pt-4">
            <TeamOverview />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
